import { CATEGORICAL_CHART_COLORS } from '@/plugins/vuetify/tokens'

/** Ordered list of hex values for chart series assignment. */
const DEFINED_COLORS: string[] = Object.values(CATEGORICAL_CHART_COLORS)

// ─── Legacy chart color palettes ─────────────────────────
// Kept for backward compatibility — registered in Vuetify theme
// so existing `:color` bindings still resolve.

const blueChart = {
  blueChart1000: '#142459',
  blueChart1010: '#174060',
  blueChart1020: '#264FA3',
  blueChart1030: '#176BA0',
  blueChart1040: '#377CB7',
  blueChart1050: '#3474EB',
  blueChart1060: '#19AADE',
  blueChart1070: '#44A5C2',
  blueChart1080: '#1AC9E6',
  blueChart1090: '#72C8F2',
  blueChart2000: '#CFEBF9'
} as const

const tealChart = {
  tealChart1000: '#1999C3',
  tealChart1010: '#19B5C3',
  tealChart1020: '#1BD4D4',
  tealChart1030: '#1DE4BD',
  tealChart1040: '#6DF0D2',
  tealChart1050: '#C7F9EE'
} as const

const purpleChart = {
  purpleChart1010: '#29066B',
  purpleChart1020: '#570088',
  purpleChart1030: '#482B8B',
  purpleChart1040: '#7D298B',
  purpleChart1050: '#7A30AB',
  purpleChart1060: '#7D3AC1',
  purpleChart1070: '#4911F6',
  purpleChart1080: '#721AF2',
  purpleChart1090: '#801FEF',
  purpleChart2000: '#B002B3',
  purpleChart2010: '#BA55D3',
  purpleChart2020: '#D8BFD8'
} as const

const redChart = {
  redChart1000: '#820401',
  redChart1010: '#9C0501',
  redChart1020: '#C02323',
  redChart1030: '#DA313F',
  redChart1040: '#DE542C',
  redChart1050: '#C5580F',
  redChart1060: '#EC553A',
  redChart1070: '#EE6F19',
  redChart1080: '#EF7E32',
  redChart1090: '#EE9319',
  redChart2000: '#F5BE74'
} as const

const greenChart = {
  greenChart1000: '#305A4C',
  greenChart1010: '#25723B',
  greenChart1020: '#1C862D',
  greenChart1030: '#08B30F',
  greenChart1040: '#61CE0A',
  greenChart1050: '#BAEA04',
  sageChart1060: '#5FAB6D',
  sageChart1070: '#8AC777',
  sageChart1080: '#A6D090',
  sageChart1090: '#CCE9B7'
} as const

const brownChart = {
  khakiChart1000: '#5F3B21',
  khakiChart1010: '#704E33',
  khakiChart1020: '#8A6B51',
  khakiChart1030: '#A48B6D',
  mudChart1040: '#985F28',
  mudChart1050: '#CD853F',
  mudChart1060: '#EE9A3A',
  mudChart1070: '#FFAE49'
} as const

const yellowChart = {
  yellowChart1000: '#EABD3B',
  yellowChart1010: '#FFCA00',
  yellowChart1020: '#FFDC00',
  yellowChart1030: '#FFE64E',
  yellowChart1040: '#FFEF89'
} as const

const pinkChart = {
  pinkChart1000: '#B03060',
  pinkChart1010: '#EC3B71',
  pinkChart1020: '#EF5FB0',
  pinkChart1030: '#F8B8DC',
  pinkChart1040: '#DB4CB2',
  pinkChart1050: '#EB48CE',
  pinkChart1060: '#F479F4',
  pinkChart1070: '#FBCCFB'
} as const

const peachChart = {
  peachChart1000: '#EA7369',
  peachChart1010: '#F99C9C',
  peachChart1030: '#F0A58F',
  peachChart1040: '#FCEAE6'
} as const

const grayChart = {
  grayChart1000: '#797979',
  grayChart1010: '#C1C1C1',
  grayChart1020: '#E5E5E5',
  warmGrayChart1030: '#A8A5A3',
  warmGrayChart1040: '#CECCCB',
  warmGrayChart1050: '#E1E0DF',
  coolGrayChart1060: '#8F97AD',
  coolGrayChart1070: '#D3D6DF',
  coolGrayChart1080: '#EAEBF0'
} as const

/** All color name → hex entries for reverse lookup (hex → name). */
const ALL_NAMED_COLORS: Record<string, string> = {
  ...CATEGORICAL_CHART_COLORS,
  ...blueChart,
  ...tealChart,
  ...purpleChart,
  ...redChart,
  ...greenChart,
  ...brownChart,
  ...yellowChart,
  ...pinkChart,
  ...peachChart,
  ...grayChart,
}

export function useChartColors() {
  function getColors(n: number): string[] {
    const len = DEFINED_COLORS.length
    if (n <= len) {
      return DEFINED_COLORS.slice(0, n)
    }
    const result: string[] = []
    const rounds = Math.floor(n / len)
    const remainder = n % len
    for (let i = 0; i < rounds; i++) {
      result.push(...DEFINED_COLORS)
    }
    result.push(...DEFINED_COLORS.slice(0, remainder))
    return result
  }

  function getNamedColorByCode(code: string): string {
    const entry = Object.entries(ALL_NAMED_COLORS).find(([, hex]) => hex === code)
    return entry?.[0] ?? ''
  }

  return { getColors, getNamedColorByCode }
}

export default {
  blueChart,
  tealChart,
  purpleChart,
  redChart,
  greenChart,
  brownChart,
  yellowChart,
  pinkChart,
  peachChart,
  grayChart
} as const
